import { useMutation } from '@apollo/client';
import type { InsertObligationInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertObligationInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertObligationInternalAuditResultInput } from './useInsertObligationInternalAuditResultTRPC';
import { useInsertObligationInternalAuditResultTRPC } from './useInsertObligationInternalAuditResultTRPC';

export const useInsertObligationInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertObligationInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_internal_audit_result');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'obligation_internal_audit_result_aggregate');
      },
    }
  );

  const trpcMutation = useInsertObligationInternalAuditResultTRPC();

  const insertObligationInternalAuditResult = async (
    variables: InsertObligationInternalAuditResultInput
  ): Promise<InsertObligationInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertObligationInternalAuditResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert obligation internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertObligationInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertObligationInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
